import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../../../contexts/ToastContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Fixed-Wing weekly rest suggestions (48h + 2 local nights) — Ops accept/dismiss.
 */
const WeeklyRestSuggestionsPanel = ({ onAccepted }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/ops/crew-duty-roster/weekly-rest-suggestions?status=PENDING`,
        { headers: authHeaders() }
      );
      setSuggestions(res.data?.suggestions || []);
    } catch (e) {
      console.error(e);
      showToast('Failed to load weekly rest suggestions', 'error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, showToast]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const handleAccept = async (id) => {
    setActingId(id);
    try {
      await axios.post(
        `${API_URL}/ops/crew-duty-roster/weekly-rest-suggestions/${id}/accept`,
        {},
        { headers: authHeaders() }
      );
      showToast('Weekly rest off block created', 'success');
      await fetchSuggestions();
      if (onAccepted) onAccepted();
    } catch (e) {
      showToast(e.response?.data?.message || 'Accept failed', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handleDismiss = async (id) => {
    setActingId(id);
    try {
      await axios.post(
        `${API_URL}/ops/crew-duty-roster/weekly-rest-suggestions/${id}/dismiss`,
        {},
        { headers: authHeaders() }
      );
      showToast('Suggestion dismissed', 'info');
      await fetchSuggestions();
    } catch (e) {
      showToast(e.response?.data?.message || 'Dismiss failed', 'error');
    } finally {
      setActingId(null);
    }
  };

  if (loading && !suggestions.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!suggestions.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Weekly rest suggestions (Fixed Wing — confirm to insert off)
      </Typography>
      <Stack spacing={1.5}>
        {suggestions.map((s) => {
          const crew = s.crewId;
          const name = crew
            ? `${crew.firstName || ''} ${crew.lastName || ''}`.trim() || crew.email
            : s.crewId;
          return (
            <Alert
              key={s._id}
              severity="warning"
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={actingId === s._id}
                    onClick={() => handleAccept(s._id)}
                  >
                    Accept
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    disabled={actingId === s._id}
                    onClick={() => handleDismiss(s._id)}
                  >
                    Dismiss
                  </Button>
                </Stack>
              }
            >
              <Typography variant="body2" fontWeight={600}>
                {name}
              </Typography>
              <Typography variant="body2">
                Proposed off:{' '}
                {new Date(s.proposedStart).toLocaleString()} →{' '}
                {new Date(s.proposedEnd).toLocaleString()} (
                {Number(s.proposedHours || 0).toFixed(1)}h, {s.localNights} local night
                {s.localNights === 1 ? '' : 's'})
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {s.reason}
              </Typography>
            </Alert>
          );
        })}
      </Stack>
    </Paper>
  );
};

export default WeeklyRestSuggestionsPanel;
